import { Document, Page, Text, View, Image, StyleSheet } from '@react-pdf/renderer';
import type { ProgressBillingReport, ProgressBillingReportColumn } from '../../utils/progressBilling';
import {
  PROGRESS_BILLING_COMPANY_NAME,
  PROGRESS_BILLING_COMPANY_WEBSITE,
  PROGRESS_BILLING_PDF_METHOD_NOTE,
  formatProgressBillingPdfCell,
  longDate,
  progressBillingCoverSubtitle,
  progressBillingPdfColumnFlex,
  progressBillingPdfColumns,
  progressBillingPdfKpis,
  progressBillingPdfRows,
  progressBillingPdfTitle,
  progressBillingPdfTotals,
} from '../../utils/progressBillingPdfLayout';

export interface ProgressBillingPdfDocumentProps {
  report: ProgressBillingReport;
  companyLogo?: string;
  companyName?: string;
  companyWebsite?: string;
  generatedDate?: Date;
}

const styles = StyleSheet.create({
  coverPage: {
    padding: 60,
    fontFamily: 'Helvetica',
    backgroundColor: '#2a392d',
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'center',
    alignItems: 'center',
    minHeight: '100%',
  },
  coverLogo: {
    width: 200,
    height: 'auto',
    marginBottom: 40,
  },
  coverCompanyFallback: {
    fontSize: 22,
    fontFamily: 'Helvetica-Bold',
    color: '#ffffff',
    marginBottom: 40,
    textAlign: 'center',
  },
  coverCard: {
    marginTop: 8,
    padding: 30,
    backgroundColor: 'rgba(255, 255, 255, 0.1)',
    borderRadius: 8,
    width: '100%',
    maxWidth: 400,
  },
  coverEyebrow: {
    fontSize: 10,
    fontFamily: 'Helvetica-Bold',
    color: 'rgba(255, 255, 255, 0.55)',
    textTransform: 'uppercase',
    letterSpacing: 1.4,
    marginBottom: 10,
    textAlign: 'center',
  },
  coverTitle: {
    fontSize: 22,
    fontFamily: 'Helvetica-Bold',
    color: '#ffffff',
    marginBottom: 8,
    textAlign: 'center',
  },
  coverSubtitle: {
    fontSize: 11,
    color: 'rgba(255, 255, 255, 0.72)',
    marginBottom: 16,
    textAlign: 'center',
    lineHeight: 1.4,
  },
  coverAsOf: {
    fontSize: 13,
    fontFamily: 'Helvetica-Bold',
    color: '#ffffff',
    marginBottom: 8,
    textAlign: 'center',
  },
  coverDate: {
    fontSize: 10,
    color: 'rgba(255, 255, 255, 0.45)',
    textAlign: 'center',
  },
  coverFoot: {
    position: 'absolute',
    bottom: 36,
    left: 60,
    right: 60,
    fontSize: 9,
    color: 'rgba(255, 255, 255, 0.4)',
    textAlign: 'center',
  },
  page: {
    paddingTop: 28,
    paddingBottom: 48,
    paddingHorizontal: 28,
    fontFamily: 'Helvetica',
    fontSize: 9,
    color: '#1a1a1a',
    backgroundColor: '#ffffff',
  },
  header: {
    backgroundColor: '#2a392d',
    paddingVertical: 12,
    paddingHorizontal: 16,
    borderRadius: 8,
    marginBottom: 14,
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
  },
  headerLeft: {
    flexDirection: 'row',
    alignItems: 'center',
    flexGrow: 1,
  },
  headerLogo: {
    width: 118,
    height: 'auto',
    marginRight: 16,
  },
  headerTitle: {
    fontSize: 16,
    fontFamily: 'Helvetica-Bold',
    color: '#ffffff',
    marginBottom: 2,
  },
  headerSubtitle: {
    fontSize: 9,
    color: 'rgba(255, 255, 255, 0.7)',
  },
  kpiRow: {
    flexDirection: 'row',
    marginBottom: 14,
  },
  kpiBox: {
    flexGrow: 1,
    flexBasis: 0,
    backgroundColor: '#f8fafc',
    borderWidth: 1,
    borderColor: '#e2e8f0',
    borderTopWidth: 3,
    borderTopColor: '#2C3E2F',
    borderRadius: 4,
    paddingVertical: 8,
    paddingHorizontal: 8,
    marginRight: 8,
  },
  kpiBoxLast: {
    marginRight: 0,
  },
  kpiLabel: {
    fontSize: 7,
    color: '#64748b',
    textTransform: 'uppercase',
    letterSpacing: 0.4,
    marginBottom: 4,
  },
  kpiValue: {
    fontSize: 10,
    fontFamily: 'Helvetica-Bold',
    color: '#1e293b',
  },
  table: {
    width: '100%',
  },
  tableHeader: {
    flexDirection: 'row',
    backgroundColor: '#1e293b',
    paddingVertical: 7,
    paddingHorizontal: 6,
    borderTopLeftRadius: 4,
    borderTopRightRadius: 4,
  },
  tableHeaderCell: {
    color: '#ffffff',
    fontSize: 7.5,
    fontFamily: 'Helvetica-Bold',
  },
  tableRow: {
    flexDirection: 'row',
    paddingVertical: 6,
    paddingHorizontal: 6,
    borderBottom: '1px solid #e2e8f0',
    backgroundColor: '#ffffff',
    alignItems: 'flex-start',
  },
  tableRowAlt: {
    backgroundColor: '#f8fafc',
  },
  tableCell: {
    fontSize: 8,
    color: '#334155',
    paddingRight: 4,
  },
  tableCellRight: {
    textAlign: 'right',
    paddingRight: 0,
    paddingLeft: 4,
  },
  totalsRow: {
    flexDirection: 'row',
    paddingVertical: 8,
    paddingHorizontal: 6,
    backgroundColor: '#1e293b',
    borderBottomLeftRadius: 4,
    borderBottomRightRadius: 4,
    alignItems: 'center',
  },
  totalsCell: {
    fontSize: 8,
    fontFamily: 'Helvetica-Bold',
    color: '#ffffff',
    paddingRight: 4,
  },
  empty: {
    padding: 18,
    backgroundColor: '#f8fafc',
    borderWidth: 1,
    borderColor: '#e2e8f0',
    borderRadius: 4,
  },
  emptyText: {
    fontSize: 10,
    color: '#64748b',
    textAlign: 'center',
  },
  methodNote: {
    marginTop: 12,
    fontSize: 8,
    color: '#64748b',
    lineHeight: 1.4,
  },
  footer: {
    position: 'absolute',
    bottom: 22,
    left: 28,
    right: 28,
    borderTop: '1px solid #e2e8f0',
    paddingTop: 8,
    flexDirection: 'row',
    justifyContent: 'space-between',
  },
  footerText: {
    fontSize: 8,
    color: '#94a3b8',
  },
  pageNumber: {
    fontSize: 8,
    color: '#94a3b8',
  },
});

function cellAlign(type: ProgressBillingReportColumn['type']): 'left' | 'right' {
  return type === 'string' || type === 'date' ? 'left' : 'right';
}

function ProgressBillingPdfDocument({
  report,
  companyLogo,
  companyName = PROGRESS_BILLING_COMPANY_NAME,
  companyWebsite = PROGRESS_BILLING_COMPANY_WEBSITE,
  generatedDate = new Date(),
}: ProgressBillingPdfDocumentProps) {
  const columns = progressBillingPdfColumns(report);
  const rows = progressBillingPdfRows(report);
  const totals = progressBillingPdfTotals(report);
  const kpis = progressBillingPdfKpis(report);
  const asOfLabel = longDate(report.asOf);
  const generatedLabel = generatedDate.toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'long',
    day: 'numeric',
  });
  const generatedTime = generatedDate.toLocaleTimeString('en-US', {
    hour: '2-digit',
    minute: '2-digit',
  });
  const subtitle = progressBillingCoverSubtitle(report.kind);
  const flexTotal = columns.reduce((sum, col) => sum + progressBillingPdfColumnFlex(col.field), 0);

  return (
    <Document
      title={progressBillingPdfTitle(report)}
      author={companyName}
      subject={subtitle}
    >
      <Page size="LETTER" style={styles.coverPage}>
        {companyLogo ? (
          <Image src={companyLogo} style={styles.coverLogo} />
        ) : (
          <Text style={styles.coverCompanyFallback}>{companyName}</Text>
        )}
        <View style={styles.coverCard}>
          <Text style={styles.coverEyebrow}>Progress Billing</Text>
          <Text style={styles.coverTitle}>{report.title}</Text>
          <Text style={styles.coverSubtitle}>{subtitle}</Text>
          <Text style={styles.coverAsOf}>As of {asOfLabel}</Text>
          <Text style={styles.coverDate}>Generated: {generatedLabel} at {generatedTime}</Text>
        </View>
        <Text style={styles.coverFoot}>{companyWebsite}</Text>
      </Page>

      <Page size="LETTER" orientation="landscape" style={styles.page}>
        <View style={styles.header} fixed>
          <View style={styles.headerLeft}>
            {companyLogo ? <Image src={companyLogo} style={styles.headerLogo} /> : null}
            <View>
              <Text style={styles.headerTitle}>{report.title}</Text>
              <Text style={styles.headerSubtitle}>As of {asOfLabel} · {subtitle}</Text>
            </View>
          </View>
        </View>

        <View style={styles.kpiRow} wrap={false}>
          {kpis.map((kpi, index) => (
            <View
              key={kpi.label}
              style={index === kpis.length - 1 ? [styles.kpiBox, styles.kpiBoxLast] : styles.kpiBox}
            >
              <Text style={styles.kpiLabel}>{kpi.label}</Text>
              <Text style={styles.kpiValue}>{kpi.value}</Text>
            </View>
          ))}
        </View>

        {rows.length === 0 ? (
          <View style={styles.empty}>
            <Text style={styles.emptyText}>No under-contract jobs as of {asOfLabel}.</Text>
          </View>
        ) : (
          <View style={styles.table}>
            <View style={styles.tableHeader} wrap={false}>
              {columns.map(col => (
                <Text
                  key={col.field}
                  style={[
                    styles.tableHeaderCell,
                    {
                      flexGrow: progressBillingPdfColumnFlex(col.field),
                      flexBasis: 0,
                      textAlign: cellAlign(col.type),
                    },
                  ]}
                >
                  {col.label}
                </Text>
              ))}
            </View>
            {rows.map((row, index) => (
              <View
                key={`${String(row.project)}-${index}`}
                style={index % 2 === 1 ? [styles.tableRow, styles.tableRowAlt] : styles.tableRow}
                wrap={false}
              >
                {columns.map(col => (
                  <Text
                    key={col.field}
                    style={[
                      styles.tableCell,
                      cellAlign(col.type) === 'right' ? styles.tableCellRight : {},
                      {
                        flexGrow: progressBillingPdfColumnFlex(col.field),
                        flexBasis: 0,
                        width: `${(progressBillingPdfColumnFlex(col.field) / flexTotal) * 100}%`,
                      },
                    ]}
                  >
                    {formatProgressBillingPdfCell(col.type, row[col.field])}
                  </Text>
                ))}
              </View>
            ))}
            <View style={styles.totalsRow} wrap={false}>
              {columns.map((col, index) => {
                const isFirst = index === 0;
                const raw = totals[col.field];
                const value = isFirst
                  ? `Totals (${rows.length})`
                  : col.type === 'currency' || col.type === 'number'
                    ? formatProgressBillingPdfCell(col.type, raw ?? 0)
                    : '';
                return (
                  <Text
                    key={col.field}
                    style={[
                      styles.totalsCell,
                      cellAlign(isFirst ? 'string' : col.type) === 'right' ? styles.tableCellRight : {},
                      {
                        flexGrow: progressBillingPdfColumnFlex(col.field),
                        flexBasis: 0,
                      },
                    ]}
                  >
                    {value}
                  </Text>
                );
              })}
            </View>
          </View>
        )}

        <Text style={styles.methodNote}>{PROGRESS_BILLING_PDF_METHOD_NOTE}</Text>

        <View style={styles.footer} fixed>
          <Text style={styles.footerText}>
            {companyName} • {companyWebsite} • {report.title}
          </Text>
          <Text
            render={({ pageNumber, totalPages }) => `Page ${pageNumber} of ${totalPages}`}
            style={styles.pageNumber}
          />
        </View>
      </Page>
    </Document>
  );
}

export default ProgressBillingPdfDocument;
export { ProgressBillingPdfDocument };
